import * as React from "react";

import { Option } from "../../../../types/Option";

import { cn } from "../../../../lib/utils";

import { Chip } from "../../Chip";

type ChipsInputProps<T> = {
	selectedOptions: Option<T>[];
	value: T[];
	onChange: (value: T[]) => void;
	isDisabled: boolean;
	placeholder: string;
	inputId: string;
	dataTestId?: string;
	renderValue?: (value: T, onDismiss?: () => void) => React.ReactNode;
};

const ChipsInput = <T,>({
	selectedOptions,
	value,
	onChange,
	isDisabled,
	placeholder,
	inputId,
	dataTestId,
	renderValue,
}: ChipsInputProps<T>) => {
	const handleChipRemove = (optionToRemove: Option<T>) => {
		return (e?: React.MouseEvent) => {
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			if (!isDisabled) {
				const newValue = value.filter((val) => val !== optionToRemove.value);
				onChange(newValue);
			}
		};
	};

	return (
		<div className="flex h-full min-w-0 flex-1 flex-wrap items-center gap-0.5 py-1 pr-0 pl-1">
			{selectedOptions.map((option, index) => {
				if (renderValue) {
					const customChip = renderValue(option.value, isDisabled ? undefined : handleChipRemove(option));

					return (
						<div
							key={`selected-${JSON.stringify(option.value)}-${index}`}
							className="flex max-w-full min-w-0 [&>*]:!flex [&>*]:max-w-full"
						>
							{customChip}
						</div>
					);
				} else {
					return (
						<Chip
							key={`selected-${JSON.stringify(option.value)}-${index}`}
							label={option.label}
							color="neutral"
							size={20}
							onDismiss={isDisabled ? undefined : handleChipRemove(option)}
							dangerouslySetClassName="!flex max-w-full"
						/>
					);
				}
			})}

			<div className="min-w-0 flex-1">
				<input
					className={cn(
						"w-full border-none bg-transparent text-paragraph-3 caret-transparent outline-hidden placeholder:text-neutral-5 focus:outline-hidden",
						isDisabled ? "cursor-not-allowed" : "cursor-pointer"
					)}
					placeholder={selectedOptions.length === 0 ? placeholder : ""}
					data-testid={dataTestId}
					disabled={isDisabled}
					id={inputId}
					readOnly
				/>
			</div>
		</div>
	);
};

ChipsInput.displayName = "ChipsInput";

export { ChipsInput };
